/**
 * SettingsModal Component
 * 
 * Modal component for configuring construction worker payslip settings.
 * Allows users to customize appearance, fonts, colors, and field visibility.
 * 
 * Features:
 * - Multiple tabs (General, Appearance, Fields, Display)
 * - Color pickers for borders and backgrounds
 * - Font size and family selection
 * - Field visibility toggles (振込, 支給, 控除, 勤怠)
 * - Select all / Deselect all functionality
 * - Settings preview
 * - Reset to defaults
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import { ConstructionPayslipSettings } from './SettingsUtils';

/**
 * Props interface for SettingsModal component.
 */
interface SettingsModalProps {
  isOpen: boolean;                            // Whether modal is open
  onClose: () => void;                        // Callback to close modal
  settings: ConstructionPayslipSettings;      // Current payslip settings
  onSettingsChange: (settings: ConstructionPayslipSettings) => void; // Callback when settings change
  furikomiItems: Array<{ id: string; name: string; keyId: string; valueId: string }>; // Furikomi field items
  paymentItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;  // Payment field items
  deductionItems: Array<{ id: string; name: string; keyId: string; valueId: string }>; // Deduction field items
  attendanceItems: Array<{ id: string; name: string; keyId: string; valueId: string }>; // Attendance field items
  notesItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;      // Notes field items
  sonotaItems: Array<{ id: string; name: string; keyId: string; valueId: string }>;    // その他 section (その他支給, その他控除, 年末調整還付金)
  bonusMode?: boolean;
}

/**
 * SettingsModal Component
 * 
 * Renders modal dialog for payslip settings configuration.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const SettingsModal: React.FC<SettingsModalProps> = ({ 
  isOpen, 
  onClose, 
  settings, 
  onSettingsChange, 
  furikomiItems, 
  paymentItems, 
  deductionItems, 
  attendanceItems, 
  notesItems,
  sonotaItems,
  bonusMode = false,
}) => {
  /**
   * State management:
   * - activeTab: Currently active main tab (general, appearance, fields, display)
   * - activeFieldTab: Currently active field type tab (furikomi, payment, deduction, attendance)
   * - originalSettings: Original settings when modal opened (for cancel functionality)
   * - tempSettings: Temporary settings being edited (updated before save)
   */
  const [activeTab, setActiveTab] = useState<'general' | 'appearance' | 'fields' | 'display'>('general');
  const [activeFieldTab, setActiveFieldTab] = useState<'furikomi' | 'payment' | 'deduction' | 'attendance' | 'notes' | 'sonota'>(
    bonusMode ? 'payment' : 'furikomi'
  );

  useEffect(() => {
    if (bonusMode && activeFieldTab === 'attendance') {
      setActiveFieldTab('payment');
    }
  }, [bonusMode, activeFieldTab]);

  /**
   * Store original settings when modal opens.
   * Used to restore settings if user cancels changes.
   */
  const [originalSettings, setOriginalSettings] = useState(settings);
  const [tempSettings, setTempSettings] = useState(settings);

  /**
   * Update temp settings when modal opens or settings prop changes.
   * 
   * Resets temporary settings to current settings when modal opens,
   * allowing user to start with fresh copy for editing.
   */
  useEffect(() => {
    setTempSettings(settings);
    if (isOpen) {
      setOriginalSettings(settings);
    }
  }, [isOpen, settings]);

  const settingsBase = tempSettings ?? settings;
  const safeSettings = {
    ...settingsBase,
    furikomi: settingsBase.furikomi || { enabled: [], lastUpdated: '', updatedBy: '' },
    payment: settingsBase.payment || { enabled: [], lastUpdated: '', updatedBy: '' },
    deduction: settingsBase.deduction || { enabled: [], lastUpdated: '', updatedBy: '' },
    attendance: settingsBase.attendance || { enabled: [], lastUpdated: '', updatedBy: '' },
    notes: settingsBase.notes || { enabled: [], lastUpdated: '', updatedBy: '' },
    sonota: settingsBase.sonota || { enabled: [], lastUpdated: '', updatedBy: '' }
  };

  /**
   * Handles color setting changes.
   * 
   * Updates temporary settings when color picker values change.
   * 
   * @param type - Type of color to change ('border', 'background', 'roumuhiBorder', 'roumuhiBackground')
   * @param value - New color value (hex color string)
   */
  const handleColorChange = (type: 'border' | 'background' | 'roumuhiBorder' | 'roumuhiBackground', value: string) => {
    const newSettings = {
      ...safeSettings,
      // Map color type to setting property name
      [type === 'border' ? 'borderColor' : 
       type === 'background' ? 'backgroundColor' :
       type === 'roumuhiBorder' ? 'roumuhiBorderColor' : 'roumuhiBackgroundColor']: value,
      // Preserve all field settings
      furikomi: safeSettings.furikomi,
      payment: safeSettings.payment,
      deduction: safeSettings.deduction,
      attendance: safeSettings.attendance,
      notes: safeSettings.notes
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles font setting changes.
   * 
   * Updates temporary settings when font size or family changes.
   * 
   * @param type - Type of font setting ('size' or 'family')
   * @param value - New font value (size string like '10px' or font family name)
   */
  const handleFontChange = (type: 'size' | 'family', value: string) => {
    const newSettings = {
      ...safeSettings,
      // Map font type to setting property name
      [type === 'size' ? 'fontSize' : 'fontFamily']: value,
      // Preserve field settings
      payment: safeSettings.payment,
      deduction: safeSettings.deduction,
      attendance: safeSettings.attendance,
      notes: safeSettings.notes
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles display setting changes.
   * 
   * Updates temporary settings when display toggles change.
   * 
   * @param type - Type of display setting ('company', 'employee', 'memo')
   * @param value - New display value (boolean)
   */
  const handleDisplayChange = (type: 'company' | 'employee' | 'memo', value: boolean) => {
    const newSettings = {
      ...safeSettings,
      // Map display type to setting property name
      [type === 'company' ? 'showCompanyName' : type === 'employee' ? 'showEmployeeCode' : 'showMemo']: value,
      // Preserve field settings
      payment: safeSettings.payment,
      deduction: safeSettings.deduction,
      attendance: safeSettings.attendance,
      notes: safeSettings.notes
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles payment field item enable/disable toggle.
   * 
   * Adds or removes an item from the enabled payment fields list.
   * Updates lastUpdated timestamp and updatedBy field.
   * 
   * @param itemId - ID of the payment item to toggle
   * @param enabled - Whether the item should be enabled
   */
  const handlePaymentItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.payment.enabled || [];
    // Add or remove item from enabled list
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      payment: {
        ...safeSettings.payment,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles deduction field item enable/disable toggle.
   * 
   * @param itemId - ID of the deduction item to toggle
   * @param enabled - Whether the item should be enabled
   */
  const handleDeductionItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.deduction.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      deduction: {
        ...safeSettings.deduction,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles attendance field item enable/disable toggle.
   * 
   * @param itemId - ID of the attendance item to toggle
   * @param enabled - Whether the item should be enabled
   */
  const handleAttendanceItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.attendance.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      attendance: {
        ...safeSettings.attendance,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles furikomi field item enable/disable toggle.
   * 
   * @param itemId - ID of the furikomi item to toggle
   * @param enabled - Whether the item should be enabled
   */
  const handleFurikomiItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.furikomi.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      furikomi: {
        ...safeSettings.furikomi,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles notes field item enable/disable toggle.
   * 
   * @param itemId - ID of the notes item to toggle
   * @param enabled - Whether the item should be enabled
   */
  const handleNotesItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.notes.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    
    const newSettings = {
      ...safeSettings,
      notes: {
        ...safeSettings.notes,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  /**
   * Handles その他 (sonota) field item enable/disable toggle.
   */
  const handleSonotaItemChange = (itemId: string, enabled: boolean) => {
    const currentEnabled = safeSettings.sonota.enabled || [];
    const newEnabled = enabled
      ? [...currentEnabled, itemId]
      : currentEnabled.filter(id => id !== itemId);
    const newSettings = {
      ...safeSettings,
      sonota: {
        ...safeSettings.sonota,
        enabled: newEnabled,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      }
    };
    setTempSettings(newSettings);
  };

  if (!isOpen) return null;

  return (
    <div className="construction-settings-modal-overlay" onClick={onClose}>
      <div className="construction-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="construction-settings-modal-header">
          <h3>労務費明細書設定</h3>
          <button className="construction-settings-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        
        <div className="construction-settings-modal-content">
          <div className="construction-settings-sidebar">
            <button 
              className={`construction-settings-nav-item ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              ✏️ 一般設定
            </button>
            <button 
              className={`construction-settings-nav-item ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              🎨 外観設定
            </button>
            <button 
              className={`construction-settings-nav-item ${activeTab === 'fields' ? 'active' : ''}`}
              onClick={() => setActiveTab('fields')}
            >
              📝 フィールド選択
            </button>
            <button 
              className={`construction-settings-nav-item ${activeTab === 'display' ? 'active' : ''}`}
              onClick={() => setActiveTab('display')}
            >
              📊  表示設定
            </button>
          </div>

          <div className="construction-settings-main-content">
            {activeTab === 'general' && (
              <div className="construction-settings-section">
                <h4>基本設定</h4>
                <div className="construction-setting-item">
                  <label>フォントサイズ:</label>
                  <select 
                    value={safeSettings.fontSize}
                    onChange={(e) => handleFontChange('size', e.target.value)}
                  >
                    <option value="6px">6px</option>
                    <option value="8px">8px</option>
                    <option value="9px">9px</option>
                    <option value="10px">10px</option>
                    <option value="11px">11px</option>
                    <option value="12px">12px</option>
                    <option value="13px">13px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                  </select>
                </div>
                <div className="construction-setting-item">
                  <label>フォント:</label>
                  <select 
                    value={safeSettings.fontFamily}
                    onChange={(e) => handleFontChange('family', e.target.value)}
                  >
                    <option value="Yu Gothic">Yu Gothic</option>
                    <option value="MS Gothic">MS Gothic</option>
                    <option value="Hiragino Kaku Gothic ProN">Hiragino Kaku Gothic ProN</option>
                    <option value="Noto Sans JP">Noto Sans JP</option>
                    <option value="Meiryo">Meiryo</option>
                  </select>
                </div>
                <div className="construction-font-preview">
                  <label>プレビュー:</label>
                  <div 
                    className="construction-font-preview-text"
                    style={{
                      fontFamily: safeSettings.fontFamily,
                      fontSize: safeSettings.fontSize
                    }}
                  >
                    給料明細書兼労務費 明細書 - 基本給 ¥300,000
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="construction-settings-section">
                <h4>色設定</h4>
                
                {/* Default Colors Section */}
                <div className="construction-setting-item">
                  <label>元の色に戻す:</label>
                  <input
                    type="checkbox" 
                    checked={safeSettings.borderColor === '#4caf50' && safeSettings.backgroundColor === '#eaffea' && safeSettings.roumuhiBorderColor === '#FF8C00' && safeSettings.roumuhiBackgroundColor === '#FFD4A3'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Reset to default colors
                        const newSettings = {
                          ...safeSettings,
                          borderColor: '#4caf50',
                          backgroundColor: '#eaffea',
                          roumuhiBorderColor: '#FF8C00',
                          roumuhiBackgroundColor: '#FFD4A3',
                          payment: safeSettings.payment,
                          deduction: safeSettings.deduction,
                          attendance: safeSettings.attendance,
                          notes: safeSettings.notes
                        };
                        onSettingsChange(newSettings);
                      }
                    }}
                  />
                </div>

                {/* Main Table Colors */}
                <div className="construction-setting-item">
                  <label>メインテーブルボーダー色:</label>
                  <input
                    type="color" 
                    value={safeSettings.borderColor}
                    onChange={(e) => handleColorChange('border', e.target.value)}
                  />
                </div>
                <div className="construction-setting-item">
                  <label>メインテーブル背景色:</label>
                  <input
                    type="color" 
                    value={safeSettings.backgroundColor}
                    onChange={(e) => handleColorChange('background', e.target.value)}
                  />
                </div>

                {/* 労務費明細書 Section Colors */}
                <div className="construction-setting-item">
                  <label>労務費明細書ボーダー色:</label>
                  <input
                    type="color"
                    value={safeSettings.roumuhiBorderColor}
                    onChange={(e) => handleColorChange('roumuhiBorder', e.target.value)}
                  />
                </div>
                <div className="construction-setting-item">
                  <label>労務費明細書背景色:</label>
                  <input
                    type="color" 
                    value={safeSettings.roumuhiBackgroundColor}
                    onChange={(e) => handleColorChange('roumuhiBackground', e.target.value)}
                  />
                </div>
              </div>
            )}

            {activeTab === 'fields' && (
              <div className="construction-settings-section">
                <h4>フィールド選択</h4>
                <div className="construction-field-selection-container">
                  <div className="construction-field-selection-tabs">
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'furikomi' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('furikomi')}
                    >
                      振込項目
                    </button>
                    {!bonusMode && (
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'attendance' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('attendance')}
                    >
                      勤怠項目
                    </button>
                    )}
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'payment' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('payment')}
                    >
                      支給項目
                    </button>
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'deduction' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('deduction')}
                    >
                      控除項目
                    </button>
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'sonota' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('sonota')}
                    >
                      その他
                    </button>
                    <button 
                      className={`construction-field-tab-btn ${activeFieldTab === 'notes' ? 'active' : ''}`}
                      onClick={() => setActiveFieldTab('notes')}
                    >
                      備考・その他
                    </button>
                  </div>

                  {activeFieldTab === 'furikomi' && (
                    <div className="construction-payment-section-header">
                      <h5>表示する振込項目を選択</h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.furikomi.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{furikomiItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFieldTab === 'payment' && (
                    <div className="construction-payment-section-header">
                      <h5>表示する支給項目を選択</h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.payment.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{paymentItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFieldTab === 'deduction' && (
                    <div className="construction-payment-section-header">
                      <h5>表示する控除項目を選択</h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.deduction.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{deductionItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFieldTab === 'attendance' && (
                    <div className="construction-payment-section-header">
                      <h5>表示する勤怠項目を選択</h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.attendance.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{attendanceItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFieldTab === 'notes' && (
                    <div className="construction-payment-section-header">
                      <h5>表示する備考・その他項目を選択</h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.notes.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{notesItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeFieldTab === 'sonota' && (
                    <div className="construction-payment-section-header">
                      <h5>
                        {bonusMode
                          ? '表示するその他項目を選択（差引支給額は常に右端に表示）'
                          : '表示するその他項目を選択（差引支給額の行に表示）'}
                      </h5>
                      <div className="construction-payment-stats">
                        <div className="construction-payment-stat">
                          選択済み: <span className="construction-payment-stat-value">{safeSettings.sonota.enabled.length}</span>
                        </div>
                        <div className="construction-payment-stat">
                          合計: <span className="construction-payment-stat-value">{sonotaItems.length}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="construction-payment-controls">
                    <label className="construction-payment-control-radio">
                      <input
                        type="radio"
                        name="construction-field-select-all"
                        checked={
                          activeFieldTab === 'furikomi' ? safeSettings.furikomi.enabled.length === furikomiItems.length :
                          activeFieldTab === 'payment' ? safeSettings.payment.enabled.length === paymentItems.length :
                          activeFieldTab === 'deduction' ? safeSettings.deduction.enabled.length === deductionItems.length :
                          activeFieldTab === 'attendance' ? safeSettings.attendance.enabled.length === attendanceItems.length :
                          activeFieldTab === 'sonota' ? safeSettings.sonota.enabled.length === sonotaItems.length :
                          activeFieldTab === 'notes' ? safeSettings.notes.enabled.length === notesItems.length :
                          false
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (activeFieldTab === 'furikomi') {
                              const newSettings = {
                                ...safeSettings,
                                furikomi: {
                                  ...safeSettings.furikomi,
                                  enabled: furikomiItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'payment') {
                              const newSettings = {
                                ...safeSettings,
                                payment: {
                                  ...safeSettings.payment,
                                  enabled: paymentItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'deduction') {
                              const newSettings = {
                                ...safeSettings,
                                deduction: {
                                  ...safeSettings.deduction,
                                  enabled: deductionItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'attendance') {
                              const newSettings = {
                                ...safeSettings,
                                attendance: {
                                  ...safeSettings.attendance,
                                  enabled: attendanceItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'sonota') {
                              const newSettings = {
                                ...safeSettings,
                                sonota: {
                                  ...safeSettings.sonota,
                                  enabled: sonotaItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'notes') {
                              const newSettings = {
                                ...safeSettings,
                                notes: {
                                  ...safeSettings.notes,
                                  enabled: notesItems.map(item => item.id),
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            }
                          }
                        }}
                      />
                      <span>すべて選択</span>
                    </label>
                    <label className="construction-payment-control-radio">
                      <input
                        type="radio"
                        name="construction-field-select-all"
                        checked={
                          activeFieldTab === 'furikomi' ? safeSettings.furikomi.enabled.length === 0 :
                          activeFieldTab === 'payment' ? safeSettings.payment.enabled.length === 0 :
                          activeFieldTab === 'deduction' ? safeSettings.deduction.enabled.length === 0 :
                          activeFieldTab === 'attendance' ? safeSettings.attendance.enabled.length === 0 :
                          activeFieldTab === 'sonota' ? safeSettings.sonota.enabled.length === 0 :
                          activeFieldTab === 'notes' ? safeSettings.notes.enabled.length === 0 :
                          false
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (activeFieldTab === 'furikomi') {
                              const newSettings = {
                                ...safeSettings,
                                furikomi: {
                                  ...safeSettings.furikomi,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'payment') {
                              const newSettings = {
                                ...safeSettings,
                                payment: {
                                  ...safeSettings.payment,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'deduction') {
                              const newSettings = {
                                ...safeSettings,
                                deduction: {
                                  ...safeSettings.deduction,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'attendance') {
                              const newSettings = {
                                ...safeSettings,
                                attendance: {
                                  ...safeSettings.attendance,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'sonota') {
                              const newSettings = {
                                ...safeSettings,
                                sonota: {
                                  ...safeSettings.sonota,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            } else if (activeFieldTab === 'notes') {
                              const newSettings = {
                                ...safeSettings,
                                notes: {
                                  ...safeSettings.notes,
                                  enabled: [],
                                  lastUpdated: new Date().toISOString(),
                                  updatedBy: 'admin'
                                }
                              };
                              onSettingsChange(newSettings);
                            }
                          }
                        }}
                      />
                      <span>すべて解除</span>
                    </label>
                  </div>

                  <div className="construction-payment-items-grid">
                    {activeFieldTab === 'furikomi' && furikomiItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.furikomi.enabled.includes(item.id)}
                          onChange={(e) => handleFurikomiItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                    {activeFieldTab === 'payment' && paymentItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.payment.enabled.includes(item.id)}
                          onChange={(e) => handlePaymentItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                    {activeFieldTab === 'deduction' && deductionItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.deduction.enabled.includes(item.id)}
                          onChange={(e) => handleDeductionItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                    {activeFieldTab === 'attendance' && attendanceItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.attendance.enabled.includes(item.id)}
                          onChange={(e) => handleAttendanceItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                    {activeFieldTab === 'sonota' && sonotaItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.sonota.enabled.includes(item.id)}
                          onChange={(e) => handleSonotaItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                    {activeFieldTab === 'notes' && notesItems.map((item) => (
                      <label key={item.id} className="construction-payment-item-checkbox">
                        <input
                          type="checkbox"
                          checked={safeSettings.notes.enabled.includes(item.id)}
                          onChange={(e) => handleNotesItemChange(item.id, e.target.checked)}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'display' && (
              <div className="construction-settings-section">
                <h4>表示設定</h4>
                
                <div className="construction-setting-item">
                  <label>会社名を表示:</label>
                  <input
                    type="checkbox"
                    checked={safeSettings.showCompanyName}
                    onChange={(e) => handleDisplayChange('company', e.target.checked)}
                  />
                </div>

                <div className="construction-setting-item">
                  <label>組織コードを表示:</label>
                  <input
                    type="checkbox"
                    checked={safeSettings.showEmployeeCode}
                    onChange={(e) => handleDisplayChange('employee', e.target.checked)}
                  />
                </div>

                <div className="construction-setting-item">
                  <label>最終セクションを表示:</label>
                  <input
                    type="checkbox"
                    checked={safeSettings.showMemo}
                    onChange={(e) => handleDisplayChange('memo', e.target.checked)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="construction-settings-modal-footer">
          <button 
            className="construction-settings-reset-btn" 
            onClick={() => {
              const resetSettings = {
                borderColor: '#4caf50',
                backgroundColor: '#eaffea',
                roumuhiBorderColor: '#FF8C00',
                roumuhiBackgroundColor: '#FFD4A3',
                fontSize: '11px',
                fontFamily: 'Yu Gothic',
                showCompanyName: true,
                showEmployeeCode: true,
                showMemo: true,
                furikomi: {
                  enabled: furikomiItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                payment: {
                  enabled: paymentItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                deduction: {
                  enabled: deductionItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                attendance: {
                  enabled: attendanceItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                notes: {
                  enabled: notesItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                },
                sonota: {
                  enabled: sonotaItems.map(item => item.id),
                  lastUpdated: new Date().toISOString(),
                  updatedBy: 'admin'
                }
              };
              setTempSettings(resetSettings);
            }}
          >
            リセット
          </button>
          <button 
            className="construction-settings-save-btn" 
            onClick={() => {
              onSettingsChange(safeSettings);
              onClose();
            }}
          >
            保存
          </button>
          <button 
            className="construction-settings-cancel-btn" 
            onClick={() => {
              setTempSettings(originalSettings);
              onClose();
            }}
          >
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
};
